// CronScheduleHeatmap.jsx - Visual heatmap of cron schedule clustering
import React, { useState, useEffect } from 'react';
import { Modal, Spin, Tooltip } from 'antd';
import api from '../functions/api';

const CronScheduleHeatmap = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [heatmapData, setHeatmapData] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [source, setSource] = useState('');

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    if (visible) {
      fetchCronSchedule();
    }
  }, [visible]);

  const fetchCronSchedule = async () => {
    setLoading(true);
    try {
      const response = await api.get('/cron_schedule_heatmap');
      setHeatmapData(response.data.heatmap);
      setSchedules(response.data.schedules || []);
      setSource(response.data.source || 'unknown');
    } catch (error) {
      console.error('Error fetching cron schedule:', error);
      setSource('error loading');
    } finally {
      setLoading(false);
    }
  };

  // Get color based on script count (0=green, 1-2=yellow, 3+=red)
  const getColor = (count) => {
    if (count === 0) return '#e8f5e9'; // light green
    if (count === 1) return '#fff9c4'; // light yellow
    if (count === 2) return '#ffe082'; // yellow
    if (count === 3) return '#ffb74d'; // orange
    return '#ef5350'; // red for 4+
  };

  const getTextColor = (count) => {
    return count >= 3 ? '#fff' : '#333';
  };

  // Find max count for legend
  const getMaxCount = () => {
    if (!heatmapData) return 0;
    let max = 0;
    for (const day of Object.values(heatmapData)) {
      for (const hour of Object.values(day)) {
        if (hour.count > max) max = hour.count;
      }
    }
    return max;
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title="Cron Schedule Heatmap"
      footer={null}
      width={1100}
      style={{ top: 20 }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : heatmapData ? (
        <div>
          <p style={{ marginBottom: 16, color: '#666', fontSize: 12 }}>
            Shows how many scripts are scheduled per hour. Red = potential clustering issues.
            Hover over cells to see script names.
            <br />
            <strong>Source:</strong> {source}
          </p>
          
          {/* Legend */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, fontSize: 11 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 16, height: 16, backgroundColor: '#e8f5e9', border: '1px solid #ccc' }} /> 0
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 16, height: 16, backgroundColor: '#fff9c4', border: '1px solid #ccc' }} /> 1
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 16, height: 16, backgroundColor: '#ffe082', border: '1px solid #ccc' }} /> 2
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 16, height: 16, backgroundColor: '#ffb74d', border: '1px solid #ccc' }} /> 3
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 16, height: 16, backgroundColor: '#ef5350', border: '1px solid #ccc' }} /> 4+
            </span>
            <span style={{ marginLeft: 16, color: '#999' }}>
              Max scripts in one slot: {getMaxCount()}
            </span>
          </div>

          {/* Heatmap Grid */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ padding: 4, textAlign: 'left', width: 50 }}>Day</th>
                  {Array.from({ length: 24 }, (_, i) => (
                    <th key={i} style={{ padding: 2, textAlign: 'center', minWidth: 36 }}>
                      {i.toString().padStart(2, '0')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dayLabels.map((dayLabel, dayIndex) => (
                  <tr key={dayIndex}>
                    <td style={{ padding: 4, fontWeight: 600 }}>{dayLabel}</td>
                    {Array.from({ length: 24 }, (_, hour) => {
                      const cell = heatmapData[dayIndex]?.[hour] || { count: 0, scripts: [] };
                      return (
                        <Tooltip
                          key={hour}
                          title={
                            cell.scripts.length > 0 ? (
                              <div>
                                <strong>{cell.count} scripts at {hour}:00</strong>
                                <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                                  {cell.scripts.map((s, i) => (
                                    <li key={i}>{s}</li>
                                  ))}
                                </ul>
                              </div>
                            ) : 'No scripts'
                          }
                        >
                          <td
                            style={{
                              backgroundColor: getColor(cell.count),
                              color: getTextColor(cell.count),
                              textAlign: 'center',
                              padding: 6,
                              border: '1px solid #e0e0e0',
                              cursor: 'pointer',
                              fontWeight: cell.count > 0 ? 600 : 400,
                            }}
                          >
                            {cell.count > 0 ? cell.count : ''}
                          </td>
                        </Tooltip>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Script List */}
          <div style={{ marginTop: 24 }}>
            <h4 style={{ marginBottom: 8 }}>Scheduled Scripts ({schedules.length})</h4>
            <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 11 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: 4, textAlign: 'left' }}>Script</th>
                    <th style={{ padding: 4, textAlign: 'left' }}>Hours</th>
                    <th style={{ padding: 4, textAlign: 'left' }}>Days</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((sched, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 4 }}>{sched.script_name}</td>
                      <td style={{ padding: 4 }}>
                        {sched.hours.length > 10 
                          ? 'Every hour' 
                          : sched.hours.map(h => h.toString().padStart(2, '0')).join(', ')}
                      </td>
                      <td style={{ padding: 4 }}>
                        {sched.days.length === 7 
                          ? 'Daily' 
                          : sched.days.map(d => dayLabels[d]).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
          No cron schedule data available
        </div>
      )}
    </Modal>
  );
};

export default CronScheduleHeatmap;
